import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { DollarSign, TrendingUp, Users, AlertCircle, ExternalLink, QrCode, Copy, CheckCircle2, Loader2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { PageHeader, TableCard, StatusBadge } from "@/components/PageHeader";

const COLORS = ["#E11D48", "#09090B", "#10B981", "#F59E0B"];

export default function Finance() {
  const [summary, setSummary] = useState(null);
  const [students, setStudents] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selStudent, setSelStudent] = useState("");
  const [selPlan, setSelPlan] = useState("");
  const [pix, setPix] = useState(null);
  const [pixLoading, setPixLoading] = useState(false);
  const [pixStatus, setPixStatus] = useState("pending");
  const [tab, setTab] = useState("visao");

  const load = async () => {
    const [f, s, p] = await Promise.all([api.get("/finance/summary"), api.get("/students"), api.get("/plans")]);
    setSummary(f.data); setStudents(s.data); setPlans(p.data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!pix?.session_id || pixStatus === "paid") return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/payments/pix/status/${pix.session_id}`);
        if (data.payment_status === "paid") { setPixStatus("paid"); toast.success("Pagamento Pix confirmado!"); load(); }
      } catch {}
    }, 3000);
    return () => clearInterval(t);
  }, [pix, pixStatus]);

  const checkoutStripe = async () => {
    if (!selStudent || !selPlan) return toast.error("Selecione aluno e plano");
    try {
      const { data } = await api.post("/payments/checkout", { plan_id: selPlan, student_id: selStudent, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const generatePix = async () => {
    if (!selStudent || !selPlan) return toast.error("Selecione aluno e plano");
    setPixLoading(true); setPixStatus("pending"); setPix(null);
    try { const { data } = await api.post("/payments/pix/create", { plan_id: selPlan, student_id: selStudent }); setPix(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro Pix"); }
    finally { setPixLoading(false); }
  };

  const cards = [
    { label: "Alunos Ativos", value: summary?.active_students ?? "-", icon: Users, color: "#10B981", testid: "finance-active-students-metric" },
    { label: "Receita Mensal", value: `R$ ${(summary?.monthly_revenue ?? 0).toFixed(2)}`, icon: DollarSign, color: "#E11D48", testid: "finance-monthly-revenue-metric" },
    { label: "Pagos Vendas Online", value: `R$ ${(summary?.stripe_revenue ?? 0).toFixed(2)}`, icon: TrendingUp, color: "#3B82F6", testid: "finance-stripe-metric" },
    { label: "Pendências", value: summary?.pending_count ?? 0, icon: AlertCircle, color: "#F59E0B", testid: "finance-pending-metric" },
  ];

  const tabs = [
    { key: "visao", label: "Visão Geral", icon: TrendingUp },
    { key: "cobrar", label: "Cobrar", icon: DollarSign },
    { key: "alunos", label: "Status Alunos", icon: Users },
  ];

  return (
    <div className="space-y-4">
      <PageHeader
        title="Financeiro"
        subtitle="Receita, mensalidades, Stripe e Pix instantâneo"
        tabs={tabs}
        activeTab={tab}
        onTabChange={setTab}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map(c => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="bg-white rounded-xl border border-slate-200 p-5 hover:border-slate-300 transition-colors" data-testid={c.testid}>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ background: c.color + "1a" }}>
                <Icon className="w-5 h-5" style={{ color: c.color }}/>
              </div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{c.label}</div>
              <div className="font-heading text-2xl font-extrabold text-slate-900 mt-1">{c.value}</div>
            </div>
          );
        })}
      </div>

      {tab === "visao" && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="font-heading font-bold text-slate-900 mb-4 uppercase tracking-tight">Alunos por plano</h3>
          <div className="h-72" data-testid="finance-plan-breakdown-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={summary?.plan_breakdown || []}>
                <XAxis dataKey="plan" tick={{ fontSize: 11 }}/>
                <YAxis tick={{ fontSize: 11 }}/>
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0" }}/>
                <Bar dataKey="students" radius={[6,6,0,0]}>
                  {(summary?.plan_breakdown || []).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]}/>)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {tab === "cobrar" && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-2xl">
          <h3 className="font-heading text-lg font-bold text-slate-900 mb-1 uppercase tracking-tight">Cobrar mensalidade</h3>
          <p className="text-sm text-slate-500 mb-4">Gere Pix instantâneo ou link Stripe para cartão.</p>
          <div className="space-y-3">
            <div><label className="text-xs uppercase text-slate-500 font-semibold">Aluno</label>
              <Select value={selStudent} onValueChange={setSelStudent}>
                <SelectTrigger data-testid="finance-student-select"><SelectValue placeholder="Selecionar aluno"/></SelectTrigger>
                <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-xs uppercase text-slate-500 font-semibold">Plano</label>
              <Select value={selPlan} onValueChange={setSelPlan}>
                <SelectTrigger data-testid="finance-plan-select"><SelectValue placeholder="Selecionar plano"/></SelectTrigger>
                <SelectContent>{plans.map(p => <SelectItem key={p.id} value={p.id}>{p.name} - R$ {p.price.toFixed(2)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2">
              <Button onClick={generatePix} disabled={pixLoading} data-testid="finance-pix-button" className="bg-emerald-600 hover:bg-emerald-700 font-bold uppercase">
                {pixLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Gerando</> : <><QrCode className="w-4 h-4 mr-2"/>Pix na tela</>}
              </Button>
              <Button onClick={checkoutStripe} data-testid="finance-stripe-checkout-button" variant="outline" className="border-rose-600 text-rose-600 hover:bg-rose-50 font-bold uppercase">
                <ExternalLink className="w-4 h-4 mr-2"/>Link cartão
              </Button>
            </div>
          </div>
        </div>
      )}

      {tab === "alunos" && (
        <TableCard>
          <div className="px-5 py-4 border-b border-slate-100"><h3 className="font-heading font-bold text-slate-900 uppercase tracking-tight">Alunos e status</h3></div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-5 py-3">Aluno</th>
                <th className="px-5 py-3">Plano</th>
                <th className="px-5 py-3">Vencimento</th>
                <th className="px-5 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {students.map(s => {
                const p = plans.find(pl => pl.id === s.plan_id);
                return (
                  <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-3 font-semibold text-slate-900">{s.name}</td>
                    <td className="px-5 py-3 text-slate-600">{p?.name || "—"}</td>
                    <td className="px-5 py-3 text-slate-600">{s.membership_end ? new Date(s.membership_end).toLocaleDateString("pt-BR") : "—"}</td>
                    <td className="px-5 py-3" data-testid="finance-payment-status-badge">
                      <StatusBadge status={s.payment_status === "paid" ? "paid" : "pending"}/>
                    </td>
                  </tr>
                );
              })}
              {students.length === 0 && <tr><td colSpan={4} className="px-5 py-12 text-center text-slate-500">Nenhum aluno</td></tr>}
            </tbody>
          </table>
        </TableCard>
      )}

      <Dialog open={!!pix} onOpenChange={async (v)=>{
        if (!v) {
          if (pix?.session_id && pixStatus !== "paid") {
            try { await api.delete(`/payments/pix/${pix.session_id}`); } catch {}
            toast.info("Tentativa de Pix cancelada");
          }
          setPix(null); setPixStatus("pending");
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><QrCode className="w-5 h-5 text-emerald-600"/>Pagamento Pix</DialogTitle></DialogHeader>
          {pix && (
            <div className="space-y-4">
              <div className="text-center">
                <div className="text-xs uppercase tracking-wider text-slate-500">Valor</div>
                <div className="font-heading text-3xl font-extrabold text-slate-900">R$ {pix.amount?.toFixed(2)}</div>
              </div>
              {pixStatus === "paid" ? (
                <div className="text-center py-6 bg-emerald-50 rounded-xl border-2 border-emerald-500" data-testid="finance-pix-paid-badge">
                  <CheckCircle2 className="w-16 h-16 text-emerald-600 mx-auto mb-2"/>
                  <div className="font-heading text-xl font-bold text-emerald-900">Pagamento confirmado!</div>
                  <p className="text-sm text-emerald-700 mt-1">Mensalidade atualizada automaticamente</p>
                </div>
              ) : (
                <>
                  {pix.is_mock && (
                    <div className="p-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800" data-testid="finance-pix-mock-banner">
                      Modo demo: Pix não está ativado na conta Stripe. Ative no <a className="underline font-semibold" href="https://dashboard.stripe.com/settings/payment_methods" target="_blank" rel="noreferrer">Dashboard Stripe</a>.
                    </div>
                  )}
                  {pix.qr_image_url && (
                    <div className="flex justify-center bg-white p-4 rounded-xl border border-slate-200">
                      <img src={pix.qr_image_url} alt="QR Pix" className="w-56 h-56" data-testid="finance-pix-qr-image"/>
                    </div>
                  )}
                  {pix.pix_code && (
                    <div>
                      <div className="text-xs font-semibold text-slate-700 mb-1">Pix Copia e Cola</div>
                      <div className="flex gap-2 items-start">
                        <div className="flex-1 p-2 bg-slate-50 rounded-lg text-xs font-mono break-all border border-slate-200 max-h-24 overflow-y-auto">{pix.pix_code}</div>
                        <Button size="sm" variant="outline" onClick={()=>{navigator.clipboard.writeText(pix.pix_code); toast.success("Copiado");}}
                          data-testid="finance-pix-copy-button"><Copy className="w-4 h-4"/></Button>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-center gap-2 text-sm text-slate-600 py-2">
                    <Loader2 className="w-4 h-4 animate-spin text-emerald-600"/>Aguardando pagamento...
                  </div>
                  {pix.is_mock && (
                    <Button size="sm" variant="outline" onClick={async ()=>{
                      try { await api.post(`/payments/pix/confirm/${pix.session_id}`); toast.success("Confirmado (demo)"); } catch { toast.error("Erro"); }
                    }} data-testid="finance-pix-mock-confirm" className="w-full">Simular pagamento (demo)</Button>
                  )}
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
